import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  RefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export interface IRecruiterDashboardContextValue {
  /** Width of the main content area in pixels */
  contentWidth: number;
  /** Ref to attach to the content wrapper element for measurement */
  contentRef: RefObject<HTMLDivElement>;
  /**
   * The URL the Donor Details tab should navigate to.
   * Set by RecruiterDashboardContent whenever filters change so that
   * clicking the tab always loads active-donors with the current filters.
   */
  donorDetailsUrl: string;
  setDonorDetailsUrl: Dispatch<SetStateAction<string>>;
  /** The last URL that actually loaded data in the Donor Details tab */
  lastDonorDetailsUrl: string;
  setLastDonorDetailsUrl: Dispatch<SetStateAction<string>>;
}

const RecruiterDashboardContext =
  createContext<IRecruiterDashboardContextValue | null>(null);

interface IProviderProps {
  children: ReactNode;
}

export const RecruiterDashboardProvider: FC<IProviderProps> = ({
  children,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [donorDetailsUrl, setDonorDetailsUrl] = useState<string>('');
  const [lastDonorDetailsUrl, setLastDonorDetailsUrl] = useState<string>('');

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    // Create ResizeObserver to track width changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Use contentBoxSize for accurate content width (excludes padding/border)
        if (entry.contentBoxSize) {
          const boxSize = Array.isArray(entry.contentBoxSize)
            ? entry.contentBoxSize[0]
            : entry.contentBoxSize;
          setContentWidth(boxSize.inlineSize);
        } else {
          // Fallback for older browsers
          setContentWidth(entry.contentRect.width);
        }
      }
    });

    resizeObserver.observe(element);

    // Also measure immediately on mount
    setContentWidth(element.clientWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const contextValue: IRecruiterDashboardContextValue = {
    contentWidth,
    contentRef,
    donorDetailsUrl,
    setDonorDetailsUrl,
    lastDonorDetailsUrl,
    setLastDonorDetailsUrl,
  };

  return (
    <RecruiterDashboardContext.Provider value={contextValue}>
      {children}
    </RecruiterDashboardContext.Provider>
  );
};

export const useRecruiterDashboard = (): IRecruiterDashboardContextValue => {
  const context = useContext(RecruiterDashboardContext);
  if (!context) {
    throw new Error(
      'useRecruiterDashboard must be used within a RecruiterDashboardProvider'
    );
  }
  return context;
};
